import React, { useContext, useEffect, useMemo, useState } from "react";
import { limit } from "firebase/firestore";
import PageWrapper from "../../../../layout/PageWrapper/PageWrapper";
import SubHeader, { SubHeaderLeft, SubHeaderRight } from "../../../../layout/SubHeader/SubHeader";
import Page from "../../../../layout/Page/Page";
import Breadcrumb from "../../../../components/bootstrap/Breadcrumb";
import Card, { CardBody, CardHeader, CardTitle } from "../../../../components/bootstrap/Card";
import Button from "../../../../components/bootstrap/Button";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import { useFormik } from "formik";
import { DefaultContext } from "../../../../contexts/default";
import Spinner from "../../../../components/bootstrap/Spinner";
import DreDB from "../../../../database/wrappers/dre";
import masks, { unmask } from "../../../../helpers/utils/masks";
import SelectEstablishment from "../../../../components/MyCustom/SelectEstablishment";
import PreAlert from "../../../../helpers/utils/preAlert";

const Dre = () => {
    const { estabSelected, onShowAlert } = useContext(DefaultContext);
    const [loading, setLoading] = useState(false);
    const [mes, setMes] = useState(new Date().getMonth() + 1);
    const [ano, setAno] = useState(new Date().getFullYear());

    const displayMoneyValue = (value) => {
        if (!value || value === 0) {
            return "";
        }
        return masks.money(value);
    }

    const createMoneyChangeHandler = (fieldName) => (e) => {
        const rawValue = e.target.value;
        const unmaskedValue = unmask(rawValue) || 0;
        formik.setFieldValue(fieldName, unmaskedValue);
    };

    const mesesDoAno = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];

    const formik = useFormik({
        initialValues: {
            impostos: { simples: 0, pis: 0, cofins: 0, iss: 0 },
            custosVariaveis: { royalties: 0, fundo_propaganda: 0, taxas_maquininha: 0, taxa_ifood: 0, embalagens: 0 },
            custosFixos: { custoPessoal: 0, ocupacional: 0, utilidades: 0, servicosTerceiros: 0, administrativa: 0 },
        },
        onSubmit: async (values) => {
            try {
                const db = new DreDB(estabSelected);
                await db.saveByMonthYear(mes, ano, values);
                onShowAlert(PreAlert.success('DRE atualizado com sucesso!'));
            }
            catch (err) {
                console.error(err);
                onShowAlert(PreAlert.success('Erro ao atualizar DRE'));
            }
        },
    });

    useEffect(() => {
        const loadDreData = async () => {
            if (!estabSelected) {
                formik.resetForm();
                return;
            }

            setLoading(true);
            try {
                const db = new DreDB(estabSelected);
                const results = await db.queryByMonthYear(mes, ano);
                if (results && results.length > 0) {
                    const data = results[0];
                    formik.setValues({
                        impostos: { ...formik.initialValues.impostos, ...data.impostos },
                        custosVariaveis: { ...formik.initialValues.custosVariaveis, ...data.custosVariaveis },
                        custosFixos: { ...formik.initialValues.custosFixos, ...data.custosFixos },
                    });
                } else {
                    formik.resetForm();
                }
            } catch (err) {
                console.error("Erro ao carregar dados do DRE:", err);
                formik.resetForm();
            } finally {
                setLoading(false);
            }
        };

        loadDreData();
    }, [estabSelected, mes, ano]);

    return (
        <PageWrapper title="DRE">
            <SubHeader>
                <SubHeaderLeft>
                    <Breadcrumb
                        list={[
                            { title: 'Cadastros', to: '/pdv/dre' },
                            {
                                title: 'Dre',
                                to: '/pdv/dre',
                            },
                        ]}
                    />
                </SubHeaderLeft>
            </SubHeader>

            <Page container="fluid">
                <Card>
                    <CardBody className='table-responsive p-5'>
                        <SelectEstablishment />
                        <div className="row mb-4">
                            <div className="col-md-3">
                                <FormGroup label="Mês">
                                    <select
                                        className="form-select"
                                        value={mes}
                                        onChange={(e) => setMes(Number(e.target.value))}
                                    >
                                        {mesesDoAno.map((nome, index) => (
                                            <option key={nome} value={index + 1}>
                                                {nome}
                                            </option>
                                        ))}
                                    </select>
                                </FormGroup>
                            </div>
                            <div className="col-md-3">
                                <FormGroup label="Ano">
                                    <select
                                        className="form-select"
                                        value={ano}
                                        onChange={(e) => setAno(Number(e.target.value))}
                                    >
                                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((a) => (
                                            <option key={a} value={a}>{a}</option>
                                        ))}
                                    </select>
                                </FormGroup>
                            </div>
                        </div>

                        {loading ? (
                            <div className="d-flex justify-content-center my-auto">
                                <Spinner className="text-center" color="blue" size={72} tag="span" />
                            </div>
                        ) : (
                            <form onSubmit={formik.handleSubmit}>

                                <Card className="mb-3">
                                    <CardHeader>
                                        <CardTitle>Impostos e Taxas</CardTitle>
                                    </CardHeader>
                                    <CardBody className="row g-3">
                                        <div className="col-md-4">
                                            <FormGroup label="Simples Nacional (R$)" id="impostos.simples">
                                                <Input
                                                    id="impostos.simples"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.impostos.simples)}
                                                    onChange={createMoneyChangeHandler('impostos.simples')}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="PIS (R$)" id="impostos.pis">
                                                <Input
                                                    id="impostos.pis"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.impostos.pis)}
                                                    onChange={createMoneyChangeHandler('impostos.pis')}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="COFINS (R$)" id="impostos.cofins">
                                                <Input
                                                    id="impostos.cofins"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.impostos.cofins)}
                                                    onChange={createMoneyChangeHandler('impostos.cofins')}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="ISS (R$)" id="impostos.iss">
                                                <Input
                                                    id="impostos.iss"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.impostos.iss)}
                                                    onChange={createMoneyChangeHandler('impostos.iss')}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </div>
                                    </CardBody>
                                </Card>

                                <Card className="mb-3">
                                    <CardHeader>
                                        <CardTitle>Custos Variáveis</CardTitle>
                                    </CardHeader>
                                    <CardBody className="row g-3">
                                        <div className="col-md-4">
                                            <FormGroup label="ROYALTIES (R$)" id="custosVariaveis.royalties">
                                                <Input
                                                    id="custosVariaveis.royalties"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosVariaveis.royalties)}
                                                    onChange={createMoneyChangeHandler('custosVariaveis.royalties')}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="FUNDO PROPAGANDA (R$)" id="custosVariaveis.fundo_propaganda">
                                                <Input
                                                    id="custosVariaveis.fundo_propaganda"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosVariaveis.fundo_propaganda)}
                                                    onChange={createMoneyChangeHandler('custosVariaveis.fundo_propaganda')}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </div>

                                        <div className="col-md-4">
                                            <FormGroup label="TAXAS ADQUIRENTE (R$)" id="custosVariaveis.taxas_maquininha">
                                                <Input
                                                    id="custosVariaveis.taxas_maquininha"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosVariaveis.taxas_maquininha)}
                                                    onChange={createMoneyChangeHandler('custosVariaveis.taxas_maquininha')}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="TAXA IFOOD (R$)" id="custosVariaveis.taxa_ifood">
                                                <Input
                                                    id="custosVariaveis.taxa_ifood"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosVariaveis.taxa_ifood)}
                                                    onChange={createMoneyChangeHandler('custosVariaveis.taxa_ifood')}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="EMBALAGENS (R$)" id="custosVariaveis.embalagens">
                                                <Input
                                                    id="custosVariaveis.embalagens"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosVariaveis.embalagens)}
                                                    onChange={createMoneyChangeHandler('custosVariaveis.embalagens')}
                                                    placeholder="R$ 0,00"
                                                />
                                            </FormGroup>
                                        </div>
                                    </CardBody>
                                </Card>

                                <Card className="mb-3">
                                    <CardHeader>
                                        <CardTitle>Custos Fixos</CardTitle>
                                    </CardHeader>
                                    <CardBody className="row g-3">
                                        <div className="col-md-4">
                                            <FormGroup label="CUSTO PESSOAL (R$)" id="custosFixos.custoPessoal">
                                                <Input
                                                    title="salários, benefícios, vale refeição, transporte, encargos sociais"
                                                    id="custosFixos.custoPessoal"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosFixos.custoPessoal)}
                                                    onChange={createMoneyChangeHandler('custosFixos.custoPessoal')}
                                                    placeholder="R$ 0,00 (salários, benefícios, vale refeição, transporte, enczargos sociais)"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="OCUPAÇÃO (R$)" id="custosFixos.ocupacional">
                                                <Input
                                                    title="aluguel, condomínio, iptu, taxas imóvel"
                                                    id="custosFixos.ocupacional"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosFixos.ocupacional)}
                                                    onChange={createMoneyChangeHandler('custosFixos.ocupacional')}
                                                    placeholder="R$ 0,00 (aluguel, condomínio, iptu, taxas imóvel)"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="UTILIDADES (R$)" id="custosFixos.utilidades">
                                                <Input
                                                    title="água, esgoto, gás, telefone, net, tv"
                                                    id="custosFixos.utilidades"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosFixos.utilidades)}
                                                    onChange={createMoneyChangeHandler('custosFixos.utilidades')}
                                                    placeholder="R$ 0,00 (água, esgoto, gás, telefone, net, tv)"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="SERVIÇOS TERCEIROS (R$)" id="custosFixos.servicosTerceiros">
                                                <Input
                                                    title="assessoria contábil, sistema, vigilância, material gráfico"
                                                    id="custosFixos.servicosTerceiros"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosFixos.servicosTerceiros)}
                                                    onChange={createMoneyChangeHandler('custosFixos.servicosTerceiros')}
                                                    placeholder="R$ 0,00 (assessoria contábil, sistema, vigilância, material gráfico)"
                                                />
                                            </FormGroup>
                                        </div>
                                        <div className="col-md-4">
                                            <FormGroup label="ADMINISTRATIVO (R$)" id="custosFixos.administrativa">
                                                <Input
                                                    title="material de limpeza, despesas administrativas, despesas adicionais"
                                                    id="custosFixos.administrativa"
                                                    type="text"
                                                    value={displayMoneyValue(formik.values.custosFixos.administrativa)}
                                                    onChange={createMoneyChangeHandler('custosFixos.administrativa')}
                                                    placeholder="R$ 0,00 (material de limpeza, despesas administrativas, despesas adicionais)"
                                                />
                                            </FormGroup>
                                        </div>
                                    </CardBody>
                                </Card>

                                <div className="text-end">
                                    <Button
                                        color="warning"
                                        type="submit"
                                        style={{ color: "black" }}
                                        disabled={loading || formik.isSubmitting}
                                    >
                                        {formik.isSubmitting ? (
                                            <>
                                                <Spinner
                                                    tag="span"
                                                    size={16}
                                                    className="me-2"
                                                />
                                                Salvando...
                                            </>
                                        ) : (
                                            "Salvar"
                                        )}
                                    </Button>
                                </div>
                            </form>
                        )}

                    </CardBody>
                </Card>
            </Page>
        </PageWrapper >
    );
};

export default Dre;